// One bar for everything, at the bottom of the screen. Type to add a widget,
// jump to one, switch board or theme, or hand the request to Claude. It reads
// what you mean: "a widget that shows…" builds one, "a theme like…" makes one,
// a question goes to Claude, and a theme you move onto is tried on the board.
// ⌘K or / opens it.

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { ArrowRight, Clock3, Copy, CornerDownLeft, Database, LayoutGrid, MessageCircle, Palette, Search, SwatchBook, Wand2 } from "lucide-react";
import { CATALOG, SPEC } from "../../shared/catalog";
import { PRESETS } from "../../shared/presets";
import { PALETTES } from "../../shared/palettes";
import { PICTURE_THEMES } from "../../shared/pictureThemes";
import { THEMES, themeDef } from "../themes/themes";
import { addWidget, switchBoard } from "../state/actions";
import { currentBoard, get, set, useStore, widgetLine, widgetLines } from "../state/store";
import { kindOf } from "../../shared/boardKinds";
import { wearTheme, wornTheme } from "../state/themes";
import { camera } from "../canvas/camera";
import { itemRect } from "../canvas/geometry";
import { cx, isMac } from "../lib/util";
import { WidgetIcon } from "./icons";
import { themeAsk, widgetAsk } from "../agent/asks";
import { askLabel, askWho, handOff, useBuilder, type Builder } from "../agent/handoff";
import { ideaOf, requestOf } from "./intent";
import { openThemePicker } from "./ThemePicker";
import { findItems, startsWord } from "./findItems";
import { useBarSlot } from "./barSlot";
import { createPortal } from "react-dom";
import { registered, useCommandSources, type Command } from "./commands/registry";
// the board's own actions, and each board kind's everyday add, join the registry as the bar loads
import "./commands/core";
import "./commands/kinds";
// SC: the slide sorter, the master, templates, Design ideas
import "./commands/deck";
import "./commands/chat";
// Fixes 9: a sheet's typed dates, and a cell's link
import "./commands/sheet";
// the bottom row it shares with getting started and zoom (Features 4, Tidy part 2)
import { useBottomRow } from "./bottomRow";
import { readStoredJson, writeStoredJson } from "../lib/stored";

type Cmd = Command;

/** Themes and live sources come first only when their name was typed, alone or among other words (Fixes 12: "sam" put a theme whose words say "same" first, and Enter wore it). */
const byNameOnly = (c: Cmd) => c.group === "Themes" || c.group === "Live sources";

function score(q: string, c: Cmd): number {
  if (!q) return 1;
  // no command is named in this many letters: a request, for the agent's row
  if (q.length > 80) return 0;
  const label = c.label.toLowerCase();
  const hay = (label + " " + c.keywords).toLowerCase();
  // "add a chart", "new note": the widget itself
  const bare = q.replace(/^(add|new|insert|put)\s+(an?\s+|the\s+)?/, "");
  if (c.group === "Add" && bare !== q && (label === bare || label.startsWith(bare))) return 90;
  if (label === q) return 100;
  if (label.startsWith(q)) return 80;
  // what was typed starts a word, never the middle of one ("rent" is not in "Current")
  if (startsWord(label, q)) return 70;
  // a theme or source named whole among other words ("ember theme")
  if (byNameOnly(c) && new RegExp(`(?:^|[^\\p{L}\\p{N}])${label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:$|[^\\p{L}\\p{N}])`, "u").test(q)) return 70;
  if (startsWord(hay, q)) return 60;
  // every word of the query starting a word somewhere
  const words = q.split(/\s+/).filter(Boolean);
  if (words.length > 1 && words.every((w) => startsWord(hay, w))) return 30;
  return 0;
}

// what was run lately, newest first, so the bar opens on it
const RECENT = "cb:cmd:recent";
function recents(): string[] {
  const r = readStoredJson<unknown>(RECENT, []);
  return Array.isArray(r) ? r.filter((x): x is string => typeof x === "string").slice(0, 5) : [];
}
function remember(id: string) {
  if (id.startsWith("jump:")) return;
  writeStoredJson(RECENT, [id, ...recents().filter((x) => x !== id)].slice(0, 5)); // private mode: not kept
}

/** A sentence carried on after "On my Context Board, ": its first letter small, but for "I" and a name in capitals. */
const carried = (t: string) => (/^(?:I\b|[A-Z]{2,}\b)/.test(t) ? t : t.charAt(0).toLowerCase() + t.slice(1));

function swatchOf(id: string) {
  const d = themeDef(id);
  const [a, b, c] = d?.swatch ?? ["#334", "#223", "#88a"];
  return <span className="cmd-swatch" style={{ background: `linear-gradient(135deg, ${a} 0 45%, ${b} 45% 100%)`, boxShadow: `inset -4px -4px 0 -1px ${c}` }} />;
}

function useCommands(builder: Builder): Cmd[] {
  const boards = useStore((s) => s.boards);
  const boardId = useStore((s) => s.boardId);
  // what each row shows, never where the widget sits: a drag on the board leaves the bar as it is
  const widgets = useStore(useShallow(widgetLines));
  const made = useStore(useShallow((s) => Object.values(s.themes)));
  const hasSelection = useStore((s) => s.selection.length > 0);
  const board = useStore(currentBoard);
  const phone = useStore((s) => s.mobile);
  // a feature that adds its actions after the bar first drew
  const sourcesV = useCommandSources((s) => s.v);
  const who = askWho(builder);
  return useMemo(() => {
    const out: Cmd[] = [];
    for (const line of widgets) {
      const { id, type, title, stream } = widgetLine(line);
      out.push({
        id: "jump:" + id,
        group: "On this board",
        label: title || SPEC[type].label,
        hint: SPEC[type].label,
        icon: <WidgetIcon type={type} size={15} />,
        keywords: `${type} ${stream} jump go find`,
        run: () => {
          // where it is now, which may not be where it was when the bar drew
          const w = get().items[id];
          if (!w) return;
          set({ selection: [id] });
          camera.fit(itemRect(w), { maxZ: 1, pad: 120 });
        },
      });
    }
    out.push(
      { id: "build", group: "Make", label: "Make a custom widget", hint: `Describe anything, and ${who} builds it`, icon: <Wand2 size={15} />, keywords: "custom widget build make create design draw anything agent code", run: () => set({ panel: "gallery", panelArg: { cat: "custom" } }) },
      { id: "make-theme", group: "Make", label: "Make a theme", hint: "Describe a place you love", icon: <Palette size={15} />, keywords: "theme look style create custom design colors colours brand", run: () => set({ panel: "theme", panelArg: { make: true } }) },
      { id: "try-themes", group: "Make", label: "Try on themes", hint: "Every theme, on the board as you go", kbd: "⇧T", icon: <SwatchBook size={15} />, keywords: "theme look style switch browse preview palette", run: () => openThemePicker() },
    );
    for (const s of CATALOG)
      out.push({
        id: "add:" + s.type,
        group: "Add",
        label: s.label,
        hint: s.blurb,
        icon: <WidgetIcon type={s.type} size={15} />,
        keywords: `add new widget ${s.keywords.join(" ")}`,
        run: () => addWidget(s.type),
      });
    // the world clock is the clock with places: asked for by name, it comes with three to start from
    out.push({
      id: "add:worldclock",
      group: "Add",
      label: "World clock",
      hint: "The time in New York, London and Tokyo, or the places you pick",
      icon: <WidgetIcon type="clock" size={15} />,
      keywords: "add new widget world clock timezones time zones cities team places",
      run: () => addWidget("clock", { title: "World clock", props: { face: "digital", zones: ["America/New_York", "Europe/London", "Asia/Tokyo"] }, w: 368, h: 232 }),
    });
    for (const p of PRESETS.filter((x) => !x.retired))
      out.push({
        id: "preset:" + p.id,
        group: "Live sources",
        label: p.label,
        hint: p.blurb,
        icon: <Database size={15} />,
        keywords: `source live data preset ${p.id} ${p.blurb}`,
        run: () => set({ panel: "connect", panelArg: { tab: "data", preset: p.id } }),
      });
    for (const b of boards)
      if (b.id !== boardId)
        out.push({ id: "board:" + b.id, group: "Boards", label: b.name, hint: "Go to board", icon: <span className="cmd-emoji">{b.emoji || "◻︎"}</span>, keywords: "board switch go", run: () => switchBoard(b.id) });
    // every theme, tried on the board while it is the chosen row
    for (const t of [...made, ...PICTURE_THEMES, ...PALETTES, ...THEMES]) {
      const by = (t as { by?: string | null }).by ?? null;
      out.push({
        id: "theme:" + t.id,
        group: "Themes",
        label: t.name,
        hint: t.blurb || (by && by !== "Context Board" ? `by ${by}` : "Theme"),
        icon: swatchOf(t.id),
        keywords: "theme look style colour color background " + (t.blurb ?? "") + (by ? " " + by : ""),
        preview: t.id,
        run: () => wearTheme(t.id),
      });
    }
    // the actions, from the registry every feature adds to (Features 4, Tidy): the board menu's, Settings', a new board
    // of each kind, the board kind's own everyday add
    out.push(...registered({ board: board ?? null, kind: kindOf(board), builder, hasSelection, phone }));
    return out;
  }, [widgets, boards, boardId, made, hasSelection, board, phone, sourcesV, builder]);
}

/** The label with the part that matched picked out. */
function Hit({ text, q }: { text: string; q: string }) {
  const at = q ? text.toLowerCase().indexOf(q) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <b className="cmd-hit">{text.slice(at, at + q.length)}</b>
      {text.slice(at + q.length)}
    </>
  );
}

export function CommandBar() {
  useBottomRow();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  // the bar rests as a slim pill on every board and widens while it is in use (David, 15 Oct, Round 2); the
  // typed boards keep their surfaces clear of it (boards.css, --bar-room)
  const tucked = false;
  // a board that keeps a place for it (a doc board's page panel) holds it there
  const slot = useBarSlot((s) => (s.el?.isConnected ? s.el : null));
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const builder = useBuilder();
  const cmds = useCommands(builder);
  const query = q.trim().toLowerCase();
  // read once for each text, not on every draw: what it asks for, and the words that ask it
  const read = useMemo(() => requestOf(q), [q]);
  const intent = read.intent;

  const { rows, lead } = useMemo(() => {
    if (!query) {
      const seen = new Set<string>();
      const take = (ids: string[], group: string) =>
        ids
          .map((id) => cmds.find((c) => c.id === id))
          .filter((c): c is Cmd => !!c && !seen.has(c.id) && !!seen.add(c.id))
          .map((c) => ({ ...c, group }));
      const worn = "theme:" + wornTheme();
      const first = ["gallery", "build", "make-theme", "try-themes", ...(builder.agent ? [] : ["connect"]), ...(builder.mode === "chat" ? ["chat"] : [])];
      return { rows: [...take(recents().filter((id) => id !== worn), "Recent"), ...take(first, "Try")], lead: false };
    }
    // a theme or a live source matched by its words, not its name, goes after everything else that matched
    const weak = (x: { c: Cmd; s: number }) => byNameOnly(x.c) && x.s < 70;
    const scored = cmds
      .map((c) => ({ c, s: score(query, c) }))
      .filter((x) => x.s > 0)
      // a nudge for what is on the board, only among things that match at all
      .map((x) => ({ c: x.c, s: x.s + (x.c.group === "On this board" ? 5 : x.c.group === "Add" ? 3 : x.c.group === "Make" ? 2 : 0) }))
      .sort((a, b) => Number(weak(a)) - Number(weak(b)) || b.s - a.s);
    // what is inside the boards (pages, ideas, posts, events, cells, tasks, deals, other boards' widgets), a few at most
    const found: Cmd[] = findItems(q, 5).map((f) => ({ ...f, group: "On your boards", keywords: "" }));
    const best = scored.slice(0, found.length ? 6 : 8).map((x) => x.c);
    // keep each group together, groups in the order their best match ranks; what is on the boards first (Fixes 12), after
    // only a command whose name starts with what was typed
    const order = [...new Set(best.map((c) => c.group))];
    const top = scored[0] && !weak(scored[0]) ? scored[0].s : 0;
    // a sentence with no strong match is a request, and a theme or source is never first unless named: Claude's row goes first
    const lead = query.length > 2 && !found.length && ((!!intent && top < 60) || (!!scored[0] && weak(scored[0])));
    let rows = order.flatMap((g) => best.filter((c) => c.group === g));
    if (found.length) {
      const first = rows.filter((c) => c.group === order[0]);
      rows = top >= 80 ? [...first, ...found, ...rows.slice(first.length)] : [...found, ...rows];
    }
    // describing a theme also offers the ones that already look like it
    if (intent === "theme") {
      const stems = ideaOf(q.slice(0, 400))
        .toLowerCase()
        .split(/[^a-z]+/)
        .filter((w) => w.length > 3 && !["like", "with", "that", "look", "theme", "feel", "into", "from"].includes(w))
        .slice(0, 12)
        .map((w) => w.replace(/(ing|ies|y|s)$/, ""));
      const near = cmds
        .filter((c) => c.preview && !rows.some((r) => r.id === c.id))
        .map((c) => ({ c, n: stems.filter((st) => (c.label + " " + (c.hint ?? "")).toLowerCase().includes(st)).length }))
        .filter((x) => x.n > 0)
        .sort((a, b) => b.n - a.n)
        .slice(0, 3)
        .map((x) => ({ ...x.c, group: "Or use one now" }));
      rows = [...near, ...rows];
    }
    return { rows, lead };
  }, [cmds, query, intent, q, builder.agent, builder.mode]);

  const agentRow = query.length > 2;
  const total = rows.length + (agentRow ? 1 : 0);
  // with the agent row first, indices shift by one
  const at = (i: number) => (lead ? (i === 0 ? -1 : i - 1) : i < rows.length ? i : -1);
  const chosen = active >= 0 && at(active) >= 0 ? rows[at(active)] : null;

  useEffect(() => setActive(0), [query]);

  // the theme under the cursor is on the board until you move off it, once
  // you have typed or moved: opening the bar on a recent theme changes nothing
  const previewing = useRef(false);
  const [moved, setMoved] = useState(false);
  useEffect(() => {
    if (!open) setMoved(false);
  }, [open]);
  useEffect(() => {
    const id = open && (query || moved) ? (chosen?.preview ?? null) : null;
    if (!id && !previewing.current) return;
    const t = setTimeout(
      () => {
        previewing.current = !!id;
        set({ themePreview: id && id !== wornTheme() ? id : null });
      },
      id ? 60 : 0,
    );
    return () => clearTimeout(t);
  }, [chosen?.id, open, moved, query]); // eslint-disable-line react-hooks/exhaustive-deps

  // keep the chosen row in view
  useEffect(() => {
    list.current?.querySelector(".cmd-row.is-active")?.scrollIntoView({ block: "nearest" });
  }, [active, rows]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable;
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !typing)) {
        e.preventDefault();
        input.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    const close = (e: PointerEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    window.addEventListener("pointerdown", close, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", close, true);
    };
  }, []);

  // anything the bar cannot do itself goes to the agent: a bare description of a widget or theme
  // as one to make, anything else in the words it was typed in
  const runAgent = () => {
    if (read.bare && intent === "theme") return handOff(themeAsk(read.words));
    if (read.bare && intent === "widget") return handOff(widgetAsk(read.words));
    const t = read.words;
    handOff({ chat: intent === "widget" || intent === "theme" ? t.charAt(0).toUpperCase() + t.slice(1) : t, agent: `On my Context Board, ${carried(t)}` });
  };

  const finish = () => {
    setQ("");
    setOpen(false);
    input.current?.blur();
  };
  const run = (i: number) => {
    const k = at(i);
    if (k >= 0) {
      const c = rows[k];
      remember(c.id);
      if (c.preview) {
        // the board is already trying it on, so keeping it needs no wipe
        previewing.current = false;
        wearTheme(c.preview, { wipe: get().themePreview !== c.preview });
        set({ themePreview: null });
      } else c.run();
    } else if (agentRow) runAgent();
    finish();
  };

  const agent = agentRow && (
    <Fragment key="agent">
      <div className="cmd-group">{lead ? "Best match" : builder.mode === "none" ? "Your agent" : askWho(builder)}</div>
      <button className={cx("cmd-row cmd-agent", active === (lead ? 0 : rows.length) && "is-active")} onMouseEnter={() => setActive(lead ? 0 : rows.length)} onClick={() => run(lead ? 0 : rows.length)}>
        <span className="cmd-ico">{builder.mode === "agent" ? <Copy size={15} /> : intent === "widget" ? <Wand2 size={15} /> : intent === "theme" ? <Palette size={15} /> : intent === "arrange" ? <LayoutGrid size={15} /> : <MessageCircle size={15} />}</span>
        <span className="cmd-label">
          {builder.mode === "agent"
            ? `Copy for ${builder.agent}`
            : intent === "widget"
              ? builder.mode === "chat"
                ? `Build it with ${builder.chat}`
                : "Build it"
              : intent === "theme"
                ? builder.mode === "chat"
                  ? `Make a theme with ${builder.chat}`
                  : "Make this theme"
                : intent === "arrange"
                  ? `Organize the board with ${askWho(builder)}`
                  : askLabel(builder)}{" "}
          <i>“{read.words}”</i>
        </span>
        <ArrowRight size={13} className="cmd-enter" />
      </button>
    </Fragment>
  );

  let lastGroup = "";
  const bar = (
    <div className={cx("cmdbar-wrap", open && "is-open", tucked && "is-tucked", slot && "in-slot")} ref={wrap}>
      {open && total > 0 && (
        <div className="cmd-results island" role="listbox" ref={list}>
          <div className="cmd-scroll">
            {lead && agent}
            {rows.map((c, k) => {
              const i = lead ? k + 1 : k;
              const head = c.group !== lastGroup ? c.group : null;
              lastGroup = c.group;
              return (
                <div key={c.id + c.group}>
                  {head && <div className="cmd-group">{head}</div>}
                  <button
                    role="option"
                    aria-selected={active === i}
                    className={cx("cmd-row", active === i && "is-active")}
                    onMouseEnter={() => {
                      setActive(i);
                      setMoved(true);
                    }}
                    onClick={() => run(i)}
                  >
                    <span className="cmd-ico">{c.icon}</span>
                    <span className="cmd-label">
                      <Hit text={c.label} q={query} />
                    </span>
                    {c.hint && <span className="cmd-hint">{c.hint}</span>}
                    <span className="cmd-end">
                      {c.group === "Recent" && <Clock3 size={12} className="cmd-recent" />}
                      {c.kbd && <kbd className="kbd">{c.kbd}</kbd>}
                      {active === i && <CornerDownLeft size={13} className="cmd-enter" />}
                    </span>
                  </button>
                </div>
              );
            })}
            {!lead && agent}
          </div>
          <div className="cmd-foot">
            <span>
              <kbd className="kbd">↑</kbd>
              <kbd className="kbd">↓</kbd> choose
            </span>
            <span>
              <kbd className="kbd">↵</kbd> {chosen?.preview ? "use it" : "run"}
            </span>
            <span>
              <kbd className="kbd">Esc</kbd> close
            </span>
            <span className="cmd-foot-tip">{builder.mode === "agent" ? `Anything else is copied for ${builder.agent}` : `Type anything to ask ${askWho(builder)}`}</span>
          </div>
        </div>
      )}
      <div className="cmdbar island">
        <Search size={15} className="cmd-search" />
        <input
          ref={input}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Find, do or ask anything"
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
              setMoved(true);
              setActive((a) => (a + 1) % Math.max(1, total));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setMoved(true);
              setActive((a) => (a - 1 + total) % Math.max(1, total));
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (total) run(active);
            } else if (e.key === "Escape") {
              finish();
            }
          }}
          aria-label="Command bar"
        />
        <kbd className="kbd">{isMac ? "⌘K" : "Ctrl K"}</kbd>
      </div>
    </div>
  );
  return slot ? createPortal(bar, slot) : bar;
}
